import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import {
	Button,
	IconButton,
	NewRequestIcon,
	Shortcut,
	Tooltip,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { generateId } from "@/lib/id"
import { useSession } from "@/lib/session"
import { mutateOk, useZero } from "@/lib/zero"

export const CreateNewThreadButton = ({
	disabled,
	companyId,
	contactId,
	withLabel = false,
	variant = "ghost",
}: {
	disabled: boolean
	companyId?: string
	contactId?: string
	withLabel?: boolean
	variant?: "ghost" | "outline"
}) => {
	const zero = useZero()
	const navigate = useNavigate()
	const { session } = useSession()
	const [isAddDisabled, setIsAddDisabled] = useState(false)

	const createThread = async () => {
		if (disabled || isAddDisabled) return
		setIsAddDisabled(true)
		try {
			const id = generateId()
			await mutateOk(
				zero.mutate(
					mutators.threads.create({
						id,
						isRead: false,
						reporterId: session?.userId ?? null,
						text: "",
						assigneeId: session?.userId ?? null,
						painLevel: "UNKNOWN",
						state: "NEW",
						origin: "INAPP",
						attachments: [],
						replied: false,
						contactIsUnverified: false,
						lastMessageAt: new Date().getTime(),
					}),
				),
			)
			if (companyId || contactId) {
				await mutateOk(
					zero.mutate(mutators.threads.update({ id, companyId, contactId })),
				)
			}
			void navigate({ to: "/threads/$id", params: { id } })
		} finally {
			setIsAddDisabled(false)
		}
		analytics.track("create-thread", { companyId, contactId })
	}

	// G N is taken by go to notifications.
	useShortcut("shift+n", () => {
		void createThread()
	})

	return (
		<Tooltip
			label={
				<div className="flex items-center gap-1">
					Create new thread
					<Shortcut>⇧</Shortcut>
					<Shortcut>N</Shortcut>
				</div>
			}
		>
			{withLabel ? (
				<Button
					variant="outline"
					size="sm"
					Icon={NewRequestIcon}
					disabled={disabled || isAddDisabled}
					onClick={() => void createThread()}
				>
					New request
				</Button>
			) : (
				<IconButton
					variant={variant}
					Icon={NewRequestIcon}
					iconClassName="h-[13px] w-[13px] !text-primary -translate-x-[0.5px]"
					disabled={disabled || isAddDisabled}
					onClick={() => void createThread()}
				/>
			)}
		</Tooltip>
	)
}
